import { Canvas } from '@react-three/fiber';
import {
  OrbitControls,
  GizmoHelper,
  GizmoViewcube,
  Stage,
  Environment,
  OrthographicCamera,
  PerspectiveCamera,
} from '@react-three/drei';
import * as THREE from 'three';
import { useState, useCallback, useEffect, useMemo } from 'react';
import { OrthographicPerspectiveToggle } from '@/components/viewer/OrthographicPerspectiveToggle';
import {
  SurfaceImageLayer,
  type TopSurfaceBounds,
} from '@/components/viewer/SurfaceImageLayer';
import { useColor } from '@/contexts/ColorContext';
import { registerViewerCanvas } from '@/lib/viewerCapture';
import { useTranslation } from 'react-i18next';
import type { DesignSpec, SurfaceImage, ViewHint } from '@shared/spec';

const DEFAULT_CAMERA_POSITION: [number, number, number] = [-100, 100, 100];

function cameraPositionForView(camera: ViewHint['camera'] | undefined): [number, number, number] {
  // Pre-rotation about X axis brings spec Z-up into ThreeScene Y-up. The
  // values below place the eye 100 units from origin on each canonical axis.
  switch (camera) {
    case 'front':
      return [0, 0, 100];
    case 'rear':
      return [0, 0, -100];
    case 'top':
      return [0, 100, 0];
    case 'bottom':
      return [0, -100, 0];
    case 'left':
      return [-100, 0, 0];
    case 'right':
      return [100, 0, 0];
    case 'isometric':
    default:
      return DEFAULT_CAMERA_POSITION;
  }
}

export function ThreeScene({
  geometry,
  viewHints,
  spec,
  surfaceImageUrls = {},
  selectedSurfaceImageId = null,
  onSurfaceImageSelect,
  onSurfaceImageChange,
  meshScale = [1, 1, 1],
}: {
  geometry: THREE.BufferGeometry;
  viewHints?: ViewHint[];
  spec?: DesignSpec;
  surfaceImageUrls?: Record<string, string>;
  selectedSurfaceImageId?: string | null;
  onSurfaceImageSelect?: (id: string | null) => void;
  onSurfaceImageChange?: (image: SurfaceImage, commit: boolean) => void;
  meshScale?: [number, number, number];
}) {
  const { t } = useTranslation('common');
  const { color } = useColor();
  const [isOrthographic, setIsOrthographic] = useState(true);

  // Pick the agent's "main" view hint when present; fall back to the first
  // hint or the default isometric pose so legacy specs still work.
  const cameraPosition = useMemo<[number, number, number]>(() => {
    if (!viewHints || viewHints.length === 0) return DEFAULT_CAMERA_POSITION;
    const main = viewHints.find((v) => v.label === 'main') ?? viewHints[0];
    return cameraPositionForView(main.camera);
  }, [viewHints]);

  const topBounds = useMemo<TopSurfaceBounds | null>(() => {
    geometry.computeBoundingBox();
    const box = geometry.boundingBox;
    if (!box) return null;
    return {
      minX: box.min.x,
      maxX: box.max.x,
      minY: box.min.y,
      maxY: box.max.y,
      topZ: box.max.z,
      width: box.max.x - box.min.x,
      depth: box.max.y - box.min.y,
    };
  }, [geometry]);

  const onCreated = useCallback(({ gl }: { gl: THREE.WebGLRenderer }) => {
    const canvas = gl.domElement;
    // Register the canvas so the auto-thumbnail capture hook can toDataURL
    // it after checkpoint commits. Cleanup in the effect below.
    registerViewerCanvas(canvas);
    // Force the renderer to preserve the buffer so toDataURL isn't blank.
    // (r3f defaults to preserveDrawingBuffer=false which breaks capture.)
    const p = gl.getContext() as WebGLRenderingContext & {
      getContextAttributes?: () => WebGLContextAttributes | null;
    };
    const attrs = p.getContextAttributes?.();
    if (attrs && !attrs.preserveDrawingBuffer) {
      console.log(
        '[ThreeScene] Note: preserveDrawingBuffer=false; toDataURL captures immediately after a frame may fail. Viewer capture uses requestAnimationFrame to retry.',
      );
    }
    const handleContextLost = (e: Event) => {
      e.preventDefault();
      console.warn('[ThreeScene] WebGL context lost — will restore');
    };
    const handleContextRestored = () => {
      console.log('[ThreeScene] WebGL context restored');
      gl.setSize(canvas.clientWidth, canvas.clientHeight);
    };
    canvas.addEventListener('webglcontextlost', handleContextLost);
    canvas.addEventListener('webglcontextrestored', handleContextRestored);
  }, []);

  useEffect(() => {
    return () => registerViewerCanvas(null);
  }, []);

  return (
    <div className="relative h-full w-full overflow-hidden">
      <Canvas className="block h-full w-full" onCreated={onCreated} gl={{ powerPreference: 'default', antialias: true, preserveDrawingBuffer: true }}>
        <color attach="background" args={['#EFE3CA']} />
        {isOrthographic ? (
          <OrthographicCamera
            key={`ortho-${cameraPosition.join(',')}`}
            makeDefault
            position={cameraPosition}
            zoom={40}
            near={0.1}
            far={1000}
          />
        ) : (
          <PerspectiveCamera
            key={`persp-${cameraPosition.join(',')}`}
            makeDefault
            position={cameraPosition}
            fov={45}
            near={0.1}
            far={1000}
            zoom={0.4}
          />
        )}
        <Stage environment={null} intensity={0.6} position={[0, 0, 0]}>
          <Environment files={`${import.meta.env.BASE_URL}city.hdr`} />
          <ambientLight intensity={0.8} />
          <directionalLight position={[5, 5, 5]} intensity={1.2} castShadow />
          <directionalLight position={[-5, 5, 5]} intensity={0.2} />
          <directionalLight position={[-5, 5, -5]} intensity={0.2} />
          <directionalLight position={[0, 5, 0]} intensity={0.2} />
          <directionalLight position={[-5, -5, -5]} intensity={0.6} />
          <mesh
            geometry={geometry}
            rotation={[-Math.PI / 2, 0, 0]}
            position={[0, 0, 0]}
            scale={meshScale}
          >
            <meshStandardMaterial
              color={color}
              metalness={0.15}
              roughness={0.6}
              envMapIntensity={0.5}
            />
          </mesh>
          <SurfaceImageLayer
            spec={spec}
            imageUrls={surfaceImageUrls}
            topBounds={topBounds}
            selectedId={selectedSurfaceImageId}
            onSelect={onSurfaceImageSelect ?? (() => {})}
            onChange={onSurfaceImageChange ?? (() => {})}
          />
        </Stage>
        {/* <Grid
          position={[0, 0, 0]}
          cellSize={30}
          cellThickness={0.5}
          sectionSize={10}
          sectionColor="gray"
          sectionThickness={0.5}
          fadeDistance={500}
          fadeStrength={1}
          followCamera={false}
          infiniteGrid={true}
        /> */}
        <OrbitControls makeDefault enableDamping={true} dampingFactor={0.05} />
        <GizmoHelper alignment="bottom-right" margin={[80, 90]}>
          <GizmoViewcube faces={['right', 'left', 'top', 'bottom', 'front', 'back'].map((k) => t(`viewer.gizmo.${k}`))} />
        </GizmoHelper>
      </Canvas>

      <div className="absolute bottom-2 right-9 flex flex-col items-center">
        <div className="flex items-center gap-2">
          <OrthographicPerspectiveToggle
            isOrthographic={isOrthographic}
            onToggle={setIsOrthographic}
          />
        </div>
      </div>
    </div>
  );
}
